import type { IAoeCart, IAoeCartItem } from '../types/CartTypes'

/**
 * Cart totals derived from the cart items.
 *
 * The cart-level fields productSubtotal, totalDiscountAmount and
 * totalBuyingDiscountAmount do not exist on the server cart model - they are
 * derived when the view model is built, and the backend has three different
 * builders that do not agree (some leave them at 0). The items collection, on
 * the other hand, is always populated, so these totals are derived here to get
 * the same number regardless of which endpoint returned the cart.
 *
 * Semantics mirror the backend (AOECartBO.CreateAOECartViewModel): every item
 * is included, discount/award lines are not excluded.
 */

function sumItems(
  cart: IAoeCart | null | undefined,
  getValue: (cartItem: IAoeCartItem) => number,
): number {
  if (!cart?.items) return 0

  return cart.items.reduce((total, cartItem) => total + getValue(cartItem), 0)
}

/** Product total before any discount: sum of price * quantity. */
export function GetCartProductSubtotal(cart: IAoeCart | null | undefined): number {
  return sumItems(cart, cartItem => cartItem.price * cartItem.quantity)
}

/** All discounts on the cart. Note this already includes the buying discount. */
export function GetCartTotalDiscountAmount(cart: IAoeCart | null | undefined): number {
  return sumItems(cart, cartItem => cartItem.totalDiscountAmount ?? 0)
}

/** Buying discount portion only. */
export function GetCartTotalBuyingDiscountAmount(cart: IAoeCart | null | undefined): number {
  return sumItems(cart, cartItem => cartItem.buyingDiscountAmount ?? 0)
}

/** Discounts other than the buying discount, shown as its own line in the order summary. */
export function GetCartOtherDiscountAmount(cart: IAoeCart | null | undefined): number {
  return GetCartTotalDiscountAmount(cart) - GetCartTotalBuyingDiscountAmount(cart)
}
